import { getDateFromNumber } from '../../../shared/components';
import {
  SORT_ACTION_MAPPING,
  TABS_MAPPING,
  VIOLTIONS_LIST_SORT_FIELDS,
} from '../../../shared/constants';
import {
  type InsightItemTypes,
  type ListRowState,
  type TrackedFunctionData,
  type TrackedListRowState,
} from '../../../shared/types';
import {
  type SodViolationsListProps,
  type SortState,
  type ViolationsTabSidePanelRowContextState,
} from '../data-layer/violationsTabTypes';

const getSodStatus = (activeTab: string | undefined) => [
  TABS_MAPPING[activeTab ?? 'openCount'],
];

interface Pagination {
  max: number;
  offset: number;
}

type FilterMapValueType = string[] | number[] | undefined;

export const getFilterPayload = (
  filterMap: Record<string, FilterMapValueType>,
  activeTab: string | undefined,
  searchKey: string | null
) => {
  // Filters
  const sodStatuses = getSodStatus(activeTab);

  // Risk payload
  const riskName = (filterMap.riskName ?? undefined) as string[];
  const riskKeys = (filterMap.riskKeys ?? undefined) as string[];
  const actualRisk = (filterMap.ACTUALVSPOTENTIAL ?? undefined) as string[];
  const violationSource = (filterMap.VIOLATIONORIGIN ?? undefined) as string[];
  const violationAge = filterMap.VIOLATIONAGE ?? undefined;
  const riskScore = filterMap.RISKSCORE ?? undefined;
  const riskPriority = (filterMap.priority ?? undefined) as string[];
  const riskType = (filterMap.riskType ?? undefined) as string[];
  const businessProcess: string[] | undefined =
    filterMap.BUSINESSPROCESS?.map((bp) => String(bp)) ?? undefined;
  const rulesetKeys: number[] | undefined =
    filterMap.RULESET?.map((rulesetKey) => Number(rulesetKey)) ?? undefined;
  const applicationName = (filterMap.APPLICATION ?? undefined) as string[];

  // User payload
  const userKeys: number[] | undefined =
    filterMap.systemUserName?.map((sysUName) => Number(sysUName)) ?? undefined;
  const userName: string[] | undefined =
    filterMap.username?.map((name) => String(name)) ?? undefined;
  const firstName = (filterMap.firstname ?? undefined) as string[];
  const lastName = (filterMap.lastname ?? undefined) as string[];
  const costCenter = (filterMap.costcenter ?? undefined) as string[];
  const employeeId: string[] | undefined =
    filterMap.employeeid?.map((empId) => String(empId)) ?? undefined;
  const jobCode: string[] | undefined =
    filterMap.jobCode?.map((jd) => String(jd)) ?? undefined;

  // Function payload
  const functionName = (filterMap.FUNCTION ?? undefined) as string[];
  const functionStatus = (filterMap.FUNCTIONSTATUS ?? undefined) as string[];

  // Account payload
  const accountName: string[] | undefined =
    filterMap.name?.map((endpoint) => String(endpoint)) ?? undefined;
  const accountType: string[] | undefined =
    filterMap.accounttype?.map((type) => String(type)) ?? undefined;
  const userLock: string[] | undefined =
    filterMap.userlock?.map((lock) => String(lock)) ?? undefined;

  // Entitlement payload
  const entitlementValue = (filterMap.entitlement_value ??
    undefined) as string[];
  const entitlementType: string[] | undefined =
    filterMap.entitlementType?.map((lock) => String(lock)) ?? undefined;
  // const soxCritical = (filterMap.SOXCRITICAL ?? []) as string[];
  // const sysCritical = (filterMap.SYSCRITICAL ?? []) as string[];
  // const privileged = (filterMap.confidentiality ?? []) as string[];

  // Mitigating controls payload
  const mitigatingControlKeys: number[] | undefined =
    filterMap.MITIGATINGCTRLNAME?.map((mcControlValue) =>
      Number(mcControlValue)
    ) ?? undefined;
  const mcExpiryDate = filterMap.MCEXPIRYDATE ?? undefined;
  const mcAssignedStartDate = filterMap.MCSTARTDATE ?? undefined;
  const mcAssignedEndDate = filterMap.MCENDDATE ?? undefined;

  // Constructed payload
  const violationAgePayload =
    violationAge !== undefined
      ? (JSON.parse(violationAge[0] as string) as (number | null)[])
      : undefined;
  const riskScorePayload =
    riskScore !== undefined
      ? (JSON.parse(riskScore[0] as string) as (number | null)[])
      : undefined;
  const mcExpiryDatePayload =
    mcExpiryDate !== undefined
      ? (JSON.parse(mcExpiryDate[0] as string) as (number | null)[])
      : undefined;
  const mcStartDatePayload =
    mcAssignedStartDate !== undefined
      ? (JSON.parse(mcAssignedStartDate[0] as string) as (number | null)[])
      : undefined;
  const mcEndDatePayload =
    mcAssignedEndDate !== undefined
      ? (JSON.parse(mcAssignedEndDate[0] as string) as (number | null)[])
      : undefined;

  const payload = {
    sodStatuses,
    risk: {
      riskName,
      riskKeys,
      actualRisk,
      riskPriority,
      riskType,
      businessProcess,
      violationSource,
      riskScore:
        riskScorePayload !== undefined
          ? {
              lessThanScore: riskScorePayload[1],
              moreThanScore: riskScorePayload[0],
            }
          : undefined,
      violationAge:
        violationAgePayload !== undefined
          ? {
              lessThanDays: violationAgePayload[1],
              moreThanDays: violationAgePayload[0],
            }
          : undefined,
    },
    ruleset: {
      rulesetKeys,
    },
    account: {
      accountName,
      accountType,
      userLock,
    },
    application: {
      applicationName,
    },
    user: {
      userKeys,
      userName,
      firstName,
      lastName,
      costCenter,
      employeeId,
      jobCode,
    },
    function: {
      functionName,
      status: functionStatus,
    },
    entitlement: {
      value: entitlementValue,
      entitlementType,
    },
    mitigatingControl: {
      mitigatingControlKeys,
      mcExpiryDate:
        mcExpiryDatePayload !== undefined
          ? {
              startDate:
                mcExpiryDatePayload[0] === null
                  ? mcExpiryDatePayload[0]
                  : getDateFromNumber(mcExpiryDatePayload[0]),
              endDate:
                mcExpiryDatePayload[1] === null
                  ? mcExpiryDatePayload[1]
                  : getDateFromNumber(mcExpiryDatePayload[1]),
            }
          : undefined,
      mcAssignedStartDate:
        mcStartDatePayload !== undefined
          ? {
              startDate:
                mcStartDatePayload[0] === null
                  ? mcStartDatePayload[0]
                  : getDateFromNumber(mcStartDatePayload[0]),
              endDate:
                mcStartDatePayload[1] === null
                  ? mcStartDatePayload[1]
                  : getDateFromNumber(mcStartDatePayload[1]),
            }
          : undefined,
      mcAssignedEndDate:
        mcEndDatePayload !== undefined
          ? {
              startDate:
                mcEndDatePayload[0] === null
                  ? mcEndDatePayload[0]
                  : getDateFromNumber(mcEndDatePayload[0]),
              endDate:
                mcEndDatePayload[1] === null
                  ? mcEndDatePayload[1]
                  : getDateFromNumber(mcEndDatePayload[1]),
            }
          : undefined,
    },
    globalSearch: `${searchKey ?? ''}`,
  };

  return payload;
};

export interface BaseFilterState {
  sodStatuses?: string[];
  risk?: {
    riskName: string[];
    riskKeys: string[];
    actualRisk: string[];
    riskPriority: string[];
    riskType: string[];
    businessProcess: string[] | undefined;
    violationSource: string[];
    riskScore:
      | {
          lessThanScore: number | null;
          moreThanScore: number | null;
        }
      | undefined;
    violationAge:
      | {
          lessThanDays: number | null;
          moreThanDays: number | null;
        }
      | undefined;
  };
  ruleset?: {
    rulesetKeys: number[] | undefined;
  };
  account?: {
    accountName: string[] | undefined;
    accountType: string[] | undefined;
    userLock: string[] | undefined;
  };
  application?: {
    applicationName: string[];
  };
  user?: {
    userKeys: number[] | undefined;
    userName: string[] | undefined;
    firstName: string[];
    lastName: string[];
    costCenter: string[];
    employeeId: string[] | undefined;
    jobCode: string[] | undefined;
  };
  function?: {
    functionName: string[];
    status: string[];
  };
  entitlement?: {
    value: string[];
    entitlementType: string[] | undefined;
  };
  mitigatingControl?: {
    mitigatingControlKeys: number[] | undefined;
    mcExpiryDate:
      | {
          startDate: string | null;
          endDate: string | null;
        }
      | undefined;
    mcAssignedStartDate:
      | {
          startDate: string | null;
          endDate: string | null;
        }
      | undefined;
    mcAssignedEndDate:
      | {
          startDate: string | null;
          endDate: string | null;
        }
      | undefined;
  };
  globalSearch: string;
}

const mergeBaseFilters = <T extends BaseFilterState | null>(
  existingFilters: T,
  newFilters: T
) => {
  try {
    if (!newFilters) {
      return existingFilters;
    }

    if (!existingFilters) {
      return newFilters;
    }

    const payload = { ...existingFilters };

    if (newFilters.risk) {
      payload.risk = { ...payload.risk, ...newFilters.risk };
    }

    if (newFilters.ruleset) {
      payload.ruleset = { ...payload.ruleset, ...newFilters.ruleset };
    }

    if (newFilters.account) {
      payload.account = { ...payload.account, ...newFilters.account };
    }

    if (newFilters.application) {
      payload.application = {
        ...payload.application,
        ...newFilters.application,
      };
    }

    if (newFilters.user) {
      payload.user = { ...payload.user, ...newFilters.user };
    }

    if (newFilters.function) {
      payload.function = { ...payload.function, ...newFilters.function };
    }

    if (newFilters.entitlement) {
      payload.entitlement = {
        ...payload.entitlement,
        ...newFilters.entitlement,
      };
    }

    if (newFilters.mitigatingControl) {
      payload.mitigatingControl = {
        ...payload.mitigatingControl,
        ...newFilters.mitigatingControl,
      };
    }

    return payload;
  } catch {
    return {};
  }
};

export const getListPayload = (
  filterMap: Record<string, FilterMapValueType>,
  activeTab: string | undefined,
  searchKey: string | null,
  sortInAction: SortState,
  activeInsight: InsightItemTypes | null,
  pagination: Pagination,
  widgetFilter: object | null = null
): SodViolationsListProps => {
  const baseFilters = getFilterPayload(filterMap, activeTab, searchKey);

  const mergedFilters = mergeBaseFilters(
    baseFilters,
    widgetFilter as BaseFilterState
  );

  // If widget is active
  if (widgetFilter) {
    const payload = {
      filters: {
        ...baseFilters,
        ...widgetFilter,
        ...mergedFilters,
      },
      pagination: {
        offset: pagination.offset,
        max: pagination.max,
      },
      sort: {
        field:
          sortInAction?.value ?? VIOLTIONS_LIST_SORT_FIELDS.UPDATEDATE.value,
        order:
          SORT_ACTION_MAPPING[sortInAction?.order ?? SORT_ACTION_MAPPING.DESC],
      },
    };

    return payload;
  }

  const payload = {
    filters: {
      ...baseFilters,
      insightRequests: activeInsight
        ? [{ id: activeInsight.id, filters: activeInsight.filters }]
        : undefined,
    },
    pagination: {
      offset: pagination.offset,
      max: pagination.max,
    },
    sort: {
      field: sortInAction?.value ?? 'sodkey',
      order:
        SORT_ACTION_MAPPING[sortInAction?.order ?? SORT_ACTION_MAPPING.DESC],
    },
  };

  return payload;
};

export const configureTracker = (
  listData: ListRowState[],
  isChecked = false,
  isReadOnlyAction = false
): TrackedListRowState[] =>
  listData.map((listRow, index) => ({
    ...listRow,
    tracker: {
      id: listRow.sodKey,
      isChecked,
      isRowDetailsOpen: false,
      isTransition: false,
      isFirstAddded: index === 0,
      isReadOnlyAction,
    },
  }));

export const getRowContext = (
  row: TrackedListRowState
): ViolationsTabSidePanelRowContextState => ({
  id: row.tracker.id,
  title: row.risk.riskName,
  displayName: row.user.displayName,
  username: row.user.username,
  allowedActions: row.userCapability.allowedActions,
  isReadOnlyActions: row.tracker.isReadOnlyAction,
  riskId: row.risk.riskId,
  rulesetName: row.risk.ruleset,
  isExpiredCount: row.appliedMcData?.totalMcExpiredCount ?? 0,
  isExpiringSoonCount: row.appliedMcData?.totalMcExpireSoon ?? 0,
  mitigatingControlCount: row.appliedMcData?.totalMcCount ?? 0,
  functions: [
    row.risk.function1 ? { key: 1, ...row.risk.function1 } : undefined,
    row.risk.function2 ? { key: 2, ...row.risk.function2 } : undefined,
    row.risk.function3 ? { key: 3, ...row.risk.function3 } : undefined,
    row.risk.function4 ? { key: 4, ...row.risk.function4 } : undefined,
    row.risk.function5 ? { key: 5, ...row.risk.function5 } : undefined,
  ].filter(Boolean) as TrackedFunctionData[],
  accounts: row.accounts,
  startDate: row.startDate,
  endDate: row.startDate,
});

export const findKeyValue = (obj: unknown, targetKey: string): string => {
  const targetKeyLower = targetKey.toLowerCase();
  const foundValues: string[] = [];

  if (typeof obj !== 'object' || obj === null) {
    return '-';
  }

  const objAsRecord = obj as Record<string, unknown>;

  // Check direct keys first
  const keys = Object.keys(objAsRecord);

  // eslint-disable-next-line @typescript-eslint/prefer-for-of, no-plusplus
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (key.toLowerCase() === targetKeyLower) {
      foundValues.push(String(objAsRecord[key]));

      // If not looking for functionName, return the first match
      if (key !== 'functionName') {
        return String(objAsRecord[key]);
      }
    }
  }

  // Recursively search nested objects
  Object.values(objAsRecord).forEach((value) => {
    if (typeof value === 'object' && value !== null) {
      const result = findKeyValue(value, targetKey);

      if (result !== '-') {
        foundValues.push(result);
      }
    }
  });

  // Return comma-separated values if found, otherwise '-'
  return foundValues.length > 0 ? foundValues.join(', ') : '-';
};

export type Primitive = string | number | boolean | null;
interface OutputItem {
  label: string;
  value: string;
  isChecked: boolean;
}

export const getUniqueLabelEntries = (
  data: Record<string, Primitive>[],
  key: string
): OutputItem[] => {
  const uniqueValues = [...new Set(data.map((item) => item[key]))];

  return uniqueValues
    .filter(Boolean) // Removes null, undefined, false, 0, '', etc.
    .filter((value) => (value !== null ? String(value).trim() : false)) // Removes whitespace-only strings
    .map((value) => ({
      label: String(value),
      value: String(value),
      isChecked: false,
    }));
};
